import { Head, router } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { SessionRecord } from '@/types'
import { MonitorIcon, SmartphoneIcon, LogOutIcon, ShieldIcon } from 'lucide-react'

interface Props {
  sessions: SessionRecord[]
}

function getDeviceIcon(userAgent: string) {
  const isMobile = /mobile|android|iphone|ipad/i.test(userAgent)
  return isMobile ? SmartphoneIcon : MonitorIcon
}

function formatUserAgent(userAgent: string): string {
  const browserMatch = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/)
  const osMatch = userAgent.match(/(Windows|Mac OS X|Linux|Android|iOS)[\s\d._]*/i)

  const browser = browserMatch ? browserMatch[1] : 'Unknown Browser'
  const os = osMatch ? osMatch[0].trim() : 'Unknown OS'

  return `${browser} on ${os}`
}

export default function Index({ sessions }: Props) {
  return (
    <>
      <Head title="Sessions" />
      <PageHeader
        title="Devices & Sessions"
        description="Manage your active sessions and devices"
      />

      <PageContent>
        <Card>
          <CardContent className="p-0">
            {sessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="rounded-full bg-muted p-3.5">
                  <ShieldIcon className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="mt-3 text-[15px] font-medium text-foreground">No active sessions</h3>
                <p className="mt-1 text-[13px] text-muted-foreground">
                  Your session information will appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Device</TableHead>
                    <TableHead className="hidden md:table-cell text-[13px] font-medium text-muted-foreground">IP Address</TableHead>
                    <TableHead className="text-[13px] font-medium text-muted-foreground">First Seen</TableHead>
                    <TableHead className="w-[100px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => {
                    const DeviceIcon = getDeviceIcon(session.user_agent)
                    return (
                      <TableRow key={session.id} className="group">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-muted p-2">
                              <DeviceIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-foreground">
                                {formatUserAgent(session.user_agent)}
                              </p>
                              <p className="text-[11px] text-muted-foreground truncate max-w-[300px]">
                                {session.user_agent}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-[13px] text-muted-foreground font-mono">
                          {session.ip_address}
                        </TableCell>
                        <TableCell className="text-[13px] text-muted-foreground tabular-nums">
                          {session.created_at}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="xs"
                            className="gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                            onClick={() => router.delete(`/sessions/${session.id}`)}
                          >
                            <LogOutIcon className="h-3 w-3" />
                            Log out
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContent>
    </>
  )
}
